import React from 'react'
import {Helmet} from "react-helmet";

import Page from '../Page'
import EmptyBanner from '../../components/EmptyBanner'
import LoadingBanner from '../../components/LoadingBanner'
import ErrorBanner from '../../components/ErrorBanner'
import StatusTabs from '../../components/nav/StatusTabs'
import ProjectPanel from './ProjectPanel'
import Project from '../../models/Project'
import CardList from "../../components/CardList"
import {ProjectFilterDropdown} from "../admin/components/StatusFilterDropdown";
import {Link, useNavigate, useParams} from "react-router-dom";
import {Select} from 'antd';
import CancelProjectModal from "./CancelProjectModal";

const { Option } = Select;
const TitleColorLookUp = {
    Active: TB.config('color.black'),
    Cancelled: TB.config('color.red'),
    Complete: TB.config('color.green'),
    Drafts: TB.config('color.blue'),
    ['Find']: TB.config('color.purple')
};

class ClientProjectsList extends Page {
    static path = '/client/projects/:status';
    static searchPlaceholder = 'Search by project name';

    static pathFor(status) {
        if (status === 'Active' || status === 'Complete') {
            status = 'Active/Complete'
        }
        return TB.pathFrom(
            this.path, {status: status === 'SOW' ? 'sow' : TB.slugify(status)}
        );
    }

    static defaultPath() {
        return this.pathFor(
            'All'
        );
    }

    static getDerivedStateFromProps(props, state) {
        let activeTab = TB.titleCase(
            props.status.replace('-', ' ')
        );
        if (props.status === 'sow') {
            activeTab = 'SOW';
        }

        if (state.active !== activeTab) {

            if (activeTab !== 'All' && activeTab !== 'Active Complete'
                && !Project.statusOptions().includes(activeTab)) {
                this.props.navigate( ClientProjectsList.defaultPath(), {replace:true})
                return null;
            }

            state.active = activeTab;
        }

        return state;
    }

    constructor(props, context) {
        super(props, context);

        let groups = TB.map(context?.auth?.client_project_groups || [], group => {
            return {
                ...group,
                value: group.id
            }
        });
        if (groups.length > 0) {
            groups.unshift(
                {
                    name: 'All',
                    value: '',
                    display: 'Filter by group'
                },
                {
                    name: 'Public (company-wide)',
                    value: -1
                }
            )
        }

        let date = TB.storedPreferences()?.date_range_filter;
        if (!date) {
            date =  TB.invert(laravel.date_limit_ranges)['All Time']
        }
        this.state = {
            ...this.state,
            isLoading: true,
            error: null,
            date: parseInt(date),
            isMobile: TB.isMobile(),
            keywordFilters: {},
            groups,
            tabs: TB.reduce(['All', ...Project.statusOptions()], (result, key) => {
                result[key] = [];
                return result
            }, {})
        };
    }

    componentDidMount() {
        super.componentDidMount();

        if (this.context?.auth?.isClientManager() || this.context?.auth?.isPlatformUser()) {
            return this.props.navigate({
                pathname: '/client/manager/projects',
            })
        }

        this.fetchProjectsForClient(true);
    }

    static handle = {
        'Projects@updated': (component) => {
            // console.log('Projects@updated')
            component.fetchProjectsForClient(true)
        },
    };

    static PageRequests = () => {
        return [
            Project.FetchAllRequest
        ]
    }

    async onKeywordsChange(keywords) {

        let keywordFilters = TB.groupBy(keywords, 'type')

        await this.promisedState({searchQuery: '', keywordFilters})

        return this.fetchProjectsForClient(true);
    }

    async fetchProjectsForClient(refresh = false) {
        this.promisedState({
            groupedProjects: undefined,
            isLoading: true,
            error: null,
        });

        let keywordFilters = {...(this.state.keywordFilters || {})};

        let params = TB.mapValues(keywordFilters, values => TB.pluck(values, 'id'));

        if (TB.isDefined(this.state.group_id) && this.state.group_id !== '') {
            params.group_id = this.state.group_id;
        }
        if (TB.isDefined(this.state.date) && this.state.date !== '') {
            await TB.persistStoredPreferences({date_range_filter: this.state.date});
            params.date = this.state.date;
        }
        // show loading feedback
        let projects = await Project.fetchAllForClient(params, refresh);
        // console.log(projects, 'PROJECCTTSS')
        if (!projects) {
            return this.handleErrorResponse(
                Project.FetchAllRequest, 'error_index_client_projects'
            );
        }

        if (!this._isMounted) {
            // console.log('fetchProjectsForClientRETURN')
            return;
        }

        let normalized = TB.map(projects, project => {
            project.display_status = project.displayStatus();
            return project;
        });

        let statusOptions = TB.values(Project.Status);
        normalized = TB.orderBy(
            normalized,
            project => statusOptions.indexOf(project.status),
            'asc'
        );

        let groupedProjects = TB.groupBy(normalized, 'display_status');

        await this.promisedState({
            groupedProjects,
            projects: normalized,
            error: null,
        });

        let tabs = {};

        for (let status of ['All', ...Project.statusOptions()]) {
            let tabTitle = Project.displayStatusFor(status),
                tabStatus = this.statusFromSlug(status);
            if (status === 'All') {
                tabs[tabTitle] = TB.flatten(Object.values(groupedProjects));
            } else if (tabTitle === 'Active' || tabTitle === 'Complete') {
                tabs['Active/Complete'] = [
                    ...(groupedProjects['Active'] || []),
                    ...(groupedProjects['Complete'] || []),
                ]
            } else {
                tabs[tabTitle] = groupedProjects[tabTitle] || []
            }
        }

        this.promisedState({
            isLoading: false,
            tabs
        });
    }

    statusFromSlug(statusSlug) {
        let status = TB.titleCase(statusSlug.replace('-', ' '));
        if (status.indexOf('Active') > -1) {
            status = 'Active/Complete';
        }
        if (status.indexOf('Sow') > -1) {
            status = 'SOW'
        }
        return status;
    }

    projectsWithStatus(statusSlug) {
        if (!this.state.groupedProjects) {
            return;
        }

        let projectStatus = this.statusFromSlug(statusSlug);

        return this.state.tabs[projectStatus] || [];
    }

    renderProjects() {
        if (!this.state.groupedProjects) {
            return <div/>
        }

        let {groupedProjects, tabs} = this.state;

        let emptyMessage = 'No Projects Found';

        if (this.props.status !== 'all') {
            let displayStatus = this.statusFromSlug(this.props.status);
            if (displayStatus === 'Sow') {
                displayStatus = 'SOW';
            }
            groupedProjects = {[displayStatus]: tabs[displayStatus]}
            emptyMessage = 'No ' + Project.displayStatusFor(displayStatus)
                + ' Projects Found';
        }
        // else {
        //     projects = groupedProjects
        //     log("PROJECTS0", groupedProjects)
        // }

        return (
            <>
                <Helmet>
                    <title>{this.statusFromSlug(this.props.status)} - Your Projects | Wripple</title>
                </Helmet>
                <div className="project-list">

                    {TB.flatten(TB.values(groupedProjects)).length === 0 ? (
                        <EmptyBanner
                            style={{paddingTop: '200px'}}
                            message={emptyMessage}/>
                    ) : TB.map(groupedProjects, (groupProjects, group) => {

                        return (
                            <div className="project-group-container"
                                 key={"group-" + (group === 'SOW' ? 'sow' : TB.slugify(group))}>

                                <CardList fillContainer={true} items={TB.orderBy(groupProjects, 'created_at', 'desc').map((project, i) => (
                                    <ProjectPanel
                                        key={`projects${i}`}
                                        project={project}
                                        groupsAvailable={this.context.auth?.clientOrg?.()?.total_groups > 0}
                                        canEdit={this.context?.auth?.canEditProjectAndPositions(project)}
                                        onToggleVisibility={() => console.log('onToggleVisibility', project)}
                                        onClickEdit={() => {
                                            if (project.isBidding() && project.canEditScope()) {
                                                return this.props.navigate(project.reviewLink())
                                            }
                                        }}
                                        onCloseProject={() => {
                                            if (this.context.auth?.canManageClients()) {
                                                return this.setState({cancelProject: project})
                                            }
                                        }}
                                    />
                                ))}>
                                    <h3 className="project-group-title"
                                        style={{color: TitleColorLookUp[group], borderColor: TitleColorLookUp[group]}}>
                                        {group}
                                    </h3>

                                </CardList>
                            </div>

                        )
                    })}

                </div>
                <div className="flex justify-center pb-12">


                    {/*<Pagination defaultCurrent={1} total={50} />*/}

                    {/*<Pagination*/}
                    {/*    total={this.state.projects?.length || 0}*/}
                    {/*    showTotal={(total) => `Total ${total} items`}*/}
                    {/*    defaultPageSize={2}*/}
                    {/*    defaultCurrent={1}*/}
                    {/*    prevIcon={<div type="button" tabIndex={-1} className="ant-pagination-item-link"><i className="fa fa-chevron-left"/></div>}*/}
                    {/*    nextIcon={<div type="button" tabIndex={-1} className="ant-pagination-item-link"><i className="fa fa-chevron-right"/></div>}*/}
                    {/*/>*/}
                </div>


                {this.context.auth?.canManageClients?.() && this.state.cancelProject && (
                    <CancelProjectModal project={this.state.cancelProject} onToggle={() => this.setState({cancelProject: null})} />
                )}

            </>

        );
    }

    renderTopBar() {
        return (
            <div className="nav-container">

                <div className="nav-info">
                    <h2 className="nav-title">
                        {TB.trans('projects.client_dashboard.nav_title')}
                    </h2>
                </div>


                <Link to={'/client/project/setup/new'}
                      className="btn btn-primary">
                    New Project
                </Link>

            </div>
        );
    }

    viewProjectsWithStatus(tab) {
        this.props.navigate({
            pathname: this.constructor.pathFor(
                tab
            )
        });
    }

    createDefaultFilter(editingFilter, options, selected, search = true,placeholder ="Filter by group") {

        if (!TB.isNaN(parseInt(selected))) {
            selected = parseInt(selected)
        }
        let selectedOption = TB.find(options, option => TB.isEqual(option.value, selected))
        if (!selectedOption) {
            selectedOption = TB.first(options);
        }

        let searchProps = {};

        if (search) {
            searchProps = {
                showSearch: true,
                onSearch: (value,...args) => console.log("onsearch", value, args),
                filterOption: (input, option) => {
                    return TB.toLower(option.key).includes(input.toLowerCase())
                }
            }
        }
        return (
            <Select
                {...searchProps}
                disabled={this.state.isDisabled || this.state.isLoading}
                placeholder={placeholder}
                onChange={async(selected, option) => {

                    if (selected !== selectedOption.value) {
                        await this.promisedState({[editingFilter]: selected});
                        return this.fetchProjectsForClient(true);
                    }

                }}
                value={selected}>

                {TB.map(options, option => (
                    <Option key={option.name} value={option.value}>{option.name}</Option>
                ))}

            </Select>
        )
    }

    async editFilter(editingFilter) {
        if (this.state.editingFilter === editingFilter) {
            editingFilter = null;
        }
        return this.promisedState({editingFilter});
    }

    render() {
        let activeStatus = this.statusFromSlug(this.props.status);

        let {
            isLoading, error, tabs, isSearching,
            isDisabled,
            searchQuery,
        } = this.state;

        // console.log(this.state.groups, 'gropuss')

        // const groupsSelectOptions = this.state.groups.map(item => {
        //     return { value: item.value, label: item.name }
        // })

        const options = Object.keys(tabs).map(tab => { return { label: tab, value: tab, projectsCount: (tabs[tab] || []).length } })

        return (
            <div className="flex-grow flex flex-col">
                {/* <Helmet>
                    <title>Your Projects | {activeStatus} | Wripple</title>
                </Helmet> */}

                {this.renderTopBar()}


                <div className="flex-grow justify-start flex flex-col items-stretch gap-[30px]">

                    {tabs && (
                        <div className="py-6 bg-gray project-status-tabs">
                            <StatusTabs
                                tabs={tabs}
                                isDisabled={isLoading}
                                activeTab={activeStatus}
                                onChange={this.viewProjectsWithStatus.bind(this)}
                            />
                        </div>
                    )}

                    <div
                        className="md:px-2 flex flex-row justify-start items-center max-w-[1140px] w-full mx-auto gap-3 client-projects-list-filters">

                        <div className="project-filter">
                            <ProjectFilterDropdown
                                className="flex-1"
                                placeholder={this.constructor.searchPlaceholder}
                                isDisabled={isDisabled || isLoading}
                                onChange={this.onKeywordsChange.bind(this)}
                            />
                        </div>


                        {this.state.groups.length > 0 && (
                            <div className="project-filter">
                                {
                                    this.createDefaultFilter(
                                        'group_id',
                                        this.state.groups,
                                        this.state.group_id
                                    )
                                }
                            </div>
                        )}
                        <div className="project-filter ml-auto">
                            {
                                this.createDefaultFilter(
                                    'date',
                                    TB.map(laravel.date_limit_ranges, (name, value) => {
                                        return {
                                            name,
                                            value: parseInt(value)
                                        }
                                    }),
                                    this.state.date,
                                    false,
                                    "Filter by Date"
                                )
                            }
                        </div>
                    </div>


                    {TB.isObject(error) ? (
                        <ErrorBanner {...error}
                                     onRetry={this.fetchProjectsForClient.bind(this)}/>
                    ) : (
                        isLoading ? <LoadingBanner message="projects.client_dashboard.loading"/> : this.renderProjects()
                    )}
                </div>

            </div>
        );
    }
}

const ProjectsList = (props) => {
    const {status} = useParams();
    const navigate = useNavigate();

    return (
        <ClientProjectsList {...props} status={status} navigate={navigate}/>
    )
}

export default ProjectsList;

const styles = {
    navBar: {
        background: '#323C47',
        alignItems: 'center',
    },

    container: {
        flexDirection: 'column'
    },

    tabBtn: {
        margin: '10px',
        borderRadius: 'unset',
        fontSize: '11px',
    },
    statusDropdown: {
        alignSelf: 'stretch',
        textAlign: 'center',
        border: '1px solid rgb(25, 42, 62)',
        boxSizing: 'border-box',
        fontWeight: 600,
        fontSize: '13px',
        lineHeight: '19px',
        letterSpacing: '0.01em',
    },
};